// The shared board model ("blackboard") for bot AI: everything expensive about
// reading the board is computed HERE, once per refresh interval, and every bot's
// scoring reads it as O(1) lookups. Bots never scan the raw collision grid
// themselves beyond the planner's existing candidate stepping.
// See docs/design/bots/PERSONALITIES.md.
//
// Contents per refresh (REFRESH_INTERVAL of game time):
//  - occupancy: the play area downsampled to a coarse tile grid (~TILES^2).
//    Advanced INCREMENTALLY by walking each track's segments from a per-track
//    time cursor and depositing painted area, so upkeep is O(new trail length),
//    never a scan of the real grid.
//  - clearance: per tile, distance in game units to the nearest blocked tile
//    (wall or substantially painted), city-block distance over the tile grid.
//  - worms: per alive worm, position/heading plus projected positions at
//    PROJECT_TIMES under current steering (arc extrapolation).
//  - powerUps: live tokens with their exact effect identity (what a player
//    reads off the token design) plus the nearest worm and its straight-line ETA.
//
// The model attaches to a gameState via WeakMap: games that never ask for it
// (online clients, games without bots) pay nothing.

import * as constants from "../core/constants.js";
import * as coreFunctions from "../core/core-functions.js";
import * as gameStateFunctions from "../core/game-state-functions.js";
import * as trajectoryUtil from "../core/geometry/trajectory/trajectory-util.js";
import * as shapeSpatialRelations from "../core/geometry/shape-spatial-relations.js";
import { crowdSettingsFor } from "./crowd.js";
import { getGridBlocks, BLOCK_SHIFT } from "../core/util/grid-blocks.js";


export var TILE_UNITS = 12.5;       // tile size in game units (the 800 map = 64x64 tiles; smaller maps get fewer tiles, same physical resolution)
export var TILES = 64;       // legacy name, kept for the playground's ENGINE line (tiles per axis on the 800 map)
export var REFRESH_INTERVAL = 0.1;  // s of game time between refreshes (small rosters)
// A crowd refreshes less often (the crowd ladder, crowd.js): the refresh is a
// full-grid pass and a projection per worm, and the bots in a big box plan on
// a longer interval anyway.
function refreshIntervalFor(gameState) {
    return crowdSettingsFor(gameState).boardRefresh;
}
var PROJECT_TIMES = [0.5, 1, 2]; // s ahead for worm projections
// Dense projected path (the trail a worm WILL lay if it keeps its current
// steering): one point per PATH_STEP out to PATH_HORIZON, clipped where the
// projection leaves the map or runs into someone else's existing trail.
export var PATH_STEP = 0.05;        // s between projected path points
export var PATH_HORIZON = 2;        // s: how far the projected path reaches
// A tile counts as blocked once this fraction of its area is painted trail.
var OCCUPANCY_BLOCK_FRACTION = 0.045;
// A worm's OWN paint from the last RECENT_OWN_WINDOW of game time (the head
// and the immune stretch right behind it) does not block that worm in its
// per-worm clearance field: it cannot crash there, so it must not read as
// "hemmed in" by its own head. Older own trail is a real obstacle and stays.
var RECENT_OWN_WINDOW = 0.35; // s
// A worm's own clearance field is computed ON DEMAND (the first time that
// worm's replan reads it after a refresh) and only in a window around its
// head: WORM_FIELD_RADIUS game units each way, enough for every point a
// candidate path can reach (the longest lookahead) plus the clearance
// reference the caution trait saturates at. Outside the window the shared
// field answers, and the window's edge counts as blocked, which can only
// understate clearance more than that radius away from the head, past what
// any lookup reads. Before this, every refresh ran a full-grid BFS for every
// alive worm, whether it planned that tick or not: a quarter of the host's
// time in a 40-seat box.
var WORM_FIELD_RADIUS = 480; // units: max lookahead (2.7s * 100) + caution reference (160) + margin

var models = new WeakMap();

// The model for `gameState`, refreshed if stale. Call from the game host's AI
// update; cheap to call every frame (WeakMap hit + one time compare).
export function getBoardModel(gameState) {
    var m = models.get(gameState);
    if (!m) {
        m = createModel(gameState);
        models.set(gameState, m);
    }
    if (gameState.gameTime - m.updatedAt >= refreshIntervalFor(gameState)) {
        refresh(m, gameState);
    }
    return m;
}

function createModel(gameState) {
    var map = gameState.map;
    var tileSize = TILE_UNITS;
    var cols = Math.max(1, Math.ceil(map.width / tileSize));
    var rows = Math.max(1, Math.ceil(map.height / tileSize));
    var n = cols * rows;
    var wall = new Uint8Array(n);
    // Walls: tiles whose center circle is not fully inside the playable shape.
    for (var r = 0; r < rows; r++) {
        for (var c = 0; c < cols; c++) {
            var cx = (c + 0.5) * tileSize;
            var cy = (r + 0.5) * tileSize;
            if (!shapeSpatialRelations.contains(map.shape, { centerX: cx, centerY: cy, radius: tileSize * 0.4 })) {
                wall[r * cols + c] = 1;
            }
        }
    }
    var m = {
        tileSize: tileSize,
        cols: cols,
        rows: rows,
        wall: wall,
        occupancy: new Float32Array(n),  // painted area (game units^2) per tile
        free: new Uint8Array(n),         // 1 = not a wall and not painted past the block threshold (refreshed once per refresh)
        clearance: new Float32Array(n),  // game units to nearest blocked tile
        spaceDir: [new Float32Array(n), new Float32Array(n), new Float32Array(n), new Float32Array(n)], // space ahead per cardinal direction (+x, +y, -x, -y)
        worms: [],                       // alive worms: {id, playerId, x, y, direction, speed, projected: [{x,y}]}
        powerUps: [],                    // {id, name, effectType, affects, x, y, nearestWormIndex, nearestEta}
        cursors: {},                     // per track: game time painted into occupancy so far
        segCursors: {},                  // per track: index of the last segment advanceOccupancy reached
        recent: {},                      // per track: [{ti, area, t}] deposits within RECENT_OWN_WINDOW
        _wormField: null,                // scratch Float32Array for the on-demand per-worm window (reused)
        updatedAt: -Infinity,
        blockArea: tileSize * tileSize * OCCUPANCY_BLOCK_FRACTION,
    };
    refresh(m, gameState);
    return m;
}

function refresh(m, gameState) {
    advanceOccupancy(m, gameState);
    // Free tiles, once per refresh (every pass below reads this instead of
    // re-deriving it from walls + occupancy).
    var free = m.free, nn = m.cols * m.rows;
    for (var fi = 0; fi < nn; fi++) {free[fi] = !m.wall[fi] && m.occupancy[fi] < m.blockArea ? 1 : 0;}
    computeWorms(m, gameState);
    computeClearance(m, m.clearance, null);
    // The four space-ahead sweeps are computed on first use per refresh (only
    // bots with the `space` trait read them; a box without one never pays).
    m.spaceStale = true;
    computePowerUps(m, gameState);
    m.updatedAt = gameState.gameTime;
}

// Deposit trail painted since each track's cursor into the occupancy tiles by
// sampling the segments' trajectories every half tile. Weight per sample is the
// painted area it represents (trail width x arc length), so the block threshold
// is resolution-independent.
function advanceOccupancy(m, gameState) {
    var tracks = gameState.wormPathSegments;
    for (var trackId in tracks) {
        var segs = tracks[trackId];
        if (!segs || !segs.length) {continue;}
        var from = m.cursors[trackId] || 0;
        var latest = 0;
        // Resume at the segment the last refresh ended on (the only one that can
        // still have grown): everything before it ended at or before `from`, so a
        // scan from index 0 (as this was) only skipped it, and cost O(trail) per
        // track per refresh, growing with the round. A track that got shorter
        // (a rewrite) starts over from 0.
        var startIndex = m.segCursors[trackId] || 0;
        if (startIndex >= segs.length) {startIndex = 0;}
        m.segCursors[trackId] = segs.length - 1;
        if (startIndex > 0) {
            var prev = segs[startIndex - 1];
            latest = prev.startTime + (prev.duration || 0);
        }
        for (var i = startIndex; i < segs.length; i++) {
            var seg = segs[i];
            var segStart = seg.startTime;
            var segEnd = seg.startTime + (seg.duration || 0);
            latest = segEnd;
            if (segEnd <= from) {continue;}
            if (!(seg.speed > 0) || seg.jump || seg.type === "still_arc" || seg.type === "worm_died") {continue;}
            var t0 = Math.max(0, from - segStart);
            var span = (segEnd - Math.max(from, segStart));
            if (span <= 0) {continue;}
            var stepUnits = m.tileSize * 0.5;
            var stepT = stepUnits / seg.speed;
            var width = 2 * (seg.size || 3);
            var recent = m.recent[trackId] || (m.recent[trackId] = []);
            for (var t = t0; t < t0 + span; t += stepT) {
                var p = trajectoryUtil.followTrajectory(seg, Math.min(t, seg.duration));
                var ti = tileIndex(m, p.x, p.y);
                if (ti >= 0) {
                    var area = width * Math.min(stepT, t0 + span - t) * seg.speed;
                    m.occupancy[ti] += area;
                    recent.push({ ti: ti, area: area, t: segStart + t });
                }
            }
        }
        m.cursors[trackId] = latest;
    }
    // Forget deposits older than the window (each track's list is time-ordered).
    var cutoff = gameState.gameTime - RECENT_OWN_WINDOW;
    for (var rid in m.recent) {
        var list = m.recent[rid];
        var drop = 0;
        while (drop < list.length && list[drop].t < cutoff) {drop++;}
        if (drop) {list.splice(0, drop);}
    }
}

// A worm's own clearance field, on demand: the tiles blocked ONLY by its own
// recent paint count as free. Computed in a window around the head (see
// WORM_FIELD_RADIUS) and cached on the board entry until the next refresh
// rebuilds the entries. With no recent own paint the shared field is exact,
// so the entry just points at it.
function ensureWormClearance(m, entry) {
    if (entry.clearance) {return entry.clearance;}
    var recent = m.recent[entry.trackId];
    if (!recent || !recent.length) {
        entry.clearance = { c0: 0, r0: 0, cols: m.cols, rows: m.rows, field: m.clearance };
        return entry.clearance;
    }
    var rad = Math.ceil(WORM_FIELD_RADIUS / m.tileSize);
    var hc = (entry.x / m.tileSize) | 0, hr = (entry.y / m.tileSize) | 0;
    var c0 = Math.max(0, hc - rad), c1 = Math.min(m.cols - 1, hc + rad);
    var r0 = Math.max(0, hr - rad), r1 = Math.min(m.rows - 1, hr + rad);
    var wc = c1 - c0 + 1, wr = r1 - r0 + 1, wn = wc * wr;
    var field = new Float32Array(wn);
    // Own recent paint, as {window index: area} to discount.
    var discount = {};
    for (var i = 0; i < recent.length; i++) {
        var ti = recent[i].ti;
        var tc = ti % m.cols, tr = (ti / m.cols) | 0;
        if (tc < c0 || tc > c1 || tr < r0 || tr > r1) {continue;}
        var wi = (tr - r0) * wc + (tc - c0);
        discount[wi] = (discount[wi] || 0) + recent[i].area;
    }
    computeWindowClearance(m, field, c0, r0, wc, wr, discount);
    entry.clearance = { c0: c0, r0: r0, cols: wc, rows: wr, field: field };
    return entry.clearance;
}

// The distance field like computeClearance, over a window of the grid: blocked
// tiles (walls, paint past the threshold minus the discount) and the window's
// edge are the sources.
function computeWindowClearance(m, dist, c0, r0, wc, wr, discount) {
    var INF = 1e9;
    var free = m.free, wall = m.wall, occupancy = m.occupancy, blockArea = m.blockArea, cols = m.cols, rows = m.rows;
    for (var r = 0; r < wr; r++) {
        // The window's edge: what lies beyond it is unknown here, so it counts as
        // blocked (unless it is the map's own edge). Row parts of that test, hoisted.
        var edgeRow = r === 0 || r === wr - 1;
        var mapEdgeRow = r0 + r === 0 || r0 + r === rows - 1;
        var gbase = (r0 + r) * cols + c0;
        for (var c = 0; c < wc; c++) {
            var wi = r * wc + c;
            var gi = gbase + c;
            var blocked = !free[gi];
            if (blocked && discount[wi] > 0 && !wall[gi] && occupancy[gi] - discount[wi] < blockArea) {blocked = false;}
            if (!blocked && (edgeRow || c === 0 || c === wc - 1) && !(mapEdgeRow || c0 + c === 0 || c0 + c === cols - 1)) {blocked = true;}
            if (blocked) { dist[wi] = 0; }
            else { dist[wi] = INF; }
        }
    }
    cityBlockSweep(dist, wc, wr, m.tileSize);
}

function computeWorms(m, gameState) {
    m.worms.length = 0;
    var worms = gameState.worms;
    for (var i = 0; i < worms.length; i++) {
        var w = worms[i];
        if (w.alive === false) {continue;}
        var speed = coreFunctions.getWormSpeed(gameState, w.id);
        var turning = 0;
        try { turning = coreFunctions.getWormTurningVelocity(gameState, w.id) || 0; } catch (e) { /* no player steering */ }
        var radius = coreFunctions.getWormRadius(gameState, w.id);
        var entry = { id: w.id, playerId: w.playerId, trackId: w.trackId, x: w.centerX, y: w.centerY, direction: w.direction, speed: speed, radius: radius, projected: [], path: null, clearance: null };
        // Arc extrapolation of the current steering; straight when not turning.
        var traj = trajectoryUtil.createTrajectory({
            startX: w.centerX, startY: w.centerY, startDirection: w.direction,
            speed: speed, turningVelocity: turning,
            duration: Math.max(PROJECT_TIMES[PROJECT_TIMES.length - 1], PATH_HORIZON),
        });
        for (var p = 0; p < PROJECT_TIMES.length; p++) {
            var pos = trajectoryUtil.followTrajectory(traj, PROJECT_TIMES[p]);
            entry.projected.push({ t: PROJECT_TIMES[p], x: pos.x, y: pos.y, direction: pos.direction });
        }
        entry.path = projectPath(gameState, w, traj, radius);
        m.worms.push(entry);
    }
}

// The trail `w` will paint if it keeps its current steering, as a dense point
// list: point i sits at time (i + 1) * step. Clipped where the projection
// would kill the worm (off the map, or into a trail that is not its own), so
// nobody fears a trail that can never be laid.
function projectPath(gameState, w, traj, radius) {
    var playArea = gameState.playArea;
    var maxN = Math.round(PATH_HORIZON / PATH_STEP);
    var xs = new Float32Array(maxN);
    var ys = new Float32Array(maxN);
    var n = 0;
    var maxGap = 0;
    // Hoisted out of the per-point loop: the worm's effects don't change within one
    // projection, and hasWormEffect filters every effect in the game on each call.
    var wallHack = gameStateFunctions.hasWormEffect(gameState, w.id, "wallHack");
    var grid = playArea.grid, gridCols = playArea.cols, gridRows = playArea.rows;
    var r2 = radius * radius;
    // Exact coarse summary of the grid (see grid-blocks.js): a point whose head only
    // covers blocks with no painted cell can't be blocked, so its cell reads are skipped.
    var blocks = getGridBlocks(playArea);
    var blockCounts = blocks.counts, bcols = blocks.bcols;
    var pos = {x: 0, y: 0};
    for (var i = 0; i < maxN; i++) {
        trajectoryUtil.followTrajectoryInto(traj, (i + 1) * PATH_STEP, pos);
        var circle = { centerX: pos.x, centerY: pos.y, radius: radius };
        if (!wallHack && !shapeSpatialRelations.contains(gameState.map.shape, circle)) {break;}
        // The head circle's cells, walked in place with the same INTERSECTION
        // rounding shapeToGridConverter uses (as checkTrajectory does), instead of
        // building a cell list per point; stops at the first blocked cell.
        var px = pos.x, py = pos.y;
        var firstRow = Math.max(0, Math.floor(py - radius));
        var lastRow = Math.min(gridRows - 1, Math.ceil(py + radius));
        var midRow = Math.round(py);
        var blocked = false;
        var anyPainted = false;
        var bc0 = Math.max(0, Math.floor(px - radius)) >> BLOCK_SHIFT;
        var bc1 = Math.min(gridCols - 1, Math.ceil(px + radius)) >> BLOCK_SHIFT;
        for (var br = firstRow >> BLOCK_SHIFT; br <= lastRow >> BLOCK_SHIFT && !anyPainted; br++) {
            for (var bc = bc0; bc <= bc1; bc++) {
                if (blockCounts[br * bcols + bc] !== 0) { anyPainted = true; break; }
            }
        }
        for (var row = firstRow; anyPainted && row <= lastRow && !blocked; row++) {
            var dy = midRow - row;
            var dx = Math.sqrt(r2 - dy * dy);
            var firstCol = Math.max(0, Math.floor(px - dx));
            var lastCol = Math.min(gridCols - 1, Math.ceil(px + dx));
            var rowBase = row * gridCols;
            for (var col = firstCol; col <= lastCol; col++) {
                var value = grid[rowBase + col];
                if (value !== constants.PLAY_AREA_FREE && value.indexOf(w.id) === -1) {blocked = true; break;}
            }
        }
        if (blocked) {break;}
        xs[n] = pos.x;
        ys[n] = pos.y;
        if (n > 0) {
            // The largest distance between consecutive stored points: readers that
            // scan the path for points near something use it to skip ahead exactly.
            var gx = xs[n] - xs[n - 1], gy = ys[n] - ys[n - 1];
            var g = Math.sqrt(gx * gx + gy * gy);
            if (g > maxGap) {maxGap = g;}
        }
        n++;
    }
    return { step: PATH_STEP, n: n, xs: xs, ys: ys, maxGap: maxGap };
}

// Distance field over the tile grid into `dist`: distance (game units,
// tile-quantized) from every tile to the nearest blocked tile (wall or painted
// past threshold). `discount` ({tile: area} or null) is paint to ignore, used
// for a worm's own recent trail.
function computeClearance(m, dist, discount) {
    var n = m.cols * m.rows;
    var INF = 1e9;
    var free = m.free;
    for (var i = 0; i < n; i++) {
        var blocked = !free[i];
        if (blocked && discount && discount[i] > 0 && !m.wall[i] && m.occupancy[i] - discount[i] < m.blockArea) {blocked = false;}
        if (blocked) {
            dist[i] = 0;
        } else {
            dist[i] = INF;
        }
    }
    cityBlockSweep(dist, m.cols, m.rows, m.tileSize);
}

// Turns a field of sources (0) and everything else (INF) into each tile's distance
// to the nearest source, in steps of `step` per tile to the side, up or down: what
// the breadth-first flood fill it replaced computed (every tile passes, every step
// costs the same, so that is the city-block distance). Two straight passes over the
// grid instead of a queue: down-right taking from above and the left, then up-left
// taking from below and the right. Exactly the same values: each is a whole number
// of steps, built by adding `step` to a neighbour's value either way.
function cityBlockSweep(dist, cols, rows, step) {
    var r, c, i, d;
    for (r = 0; r < rows; r++) {
        i = r * cols;
        for (c = 0; c < cols; c++, i++) {
            if (r > 0) { d = dist[i - cols] + step; if (d < dist[i]) {dist[i] = d;} }
            if (c > 0) { d = dist[i - 1] + step; if (d < dist[i]) {dist[i] = d;} }
        }
    }
    for (r = rows - 1; r >= 0; r--) {
        i = r * cols + cols - 1;
        for (c = cols - 1; c >= 0; c--, i--) {
            if (r < rows - 1) { d = dist[i + cols] + step; if (d < dist[i]) {dist[i] = d;} }
            if (c < cols - 1) { d = dist[i + 1] + step; if (d < dist[i]) {dist[i] = d;} }
        }
    }
}




// SPACE AHEAD, one field per cardinal direction, shared by all worms. A sweep
// against the direction of travel: a free tile's value is 1 + the weighted
// value of the three tiles ahead of it (straight ahead 1/2, the two forward
// diagonals 1/4 each), capped at SPACE_CAP; blocked tiles and the board edge
// are 0. The result is the average free depth ahead within a widening 90
// degree cone: a wall ahead reads short, a pocket shallow, open space full.
// O(tiles) per direction.
var SPACE_CAP = 24; // tiles (~300 units): depth ahead that reads as full space
function computeSpaceFields(m) {
    var cols = m.cols, rows = m.rows;
    var free = m.free;
    var f = m.spaceDir;
    // 0: +x (right), 1: +y (down), 2: -x (left), 3: -y (up). Each sweep runs against
    // its direction so the tiles ahead are mostly done (the visiting order is part of
    // the result: a diagonal can still hold the previous refresh's value). One loop
    // per direction with the edge checks hoisted: this runs over every tile four
    // times per refresh. Same order and arithmetic as the general form it replaced.
    var r, c, i, v, ahead, side1, side2, val;
    // +x: rows top to bottom, columns right to left; ahead (r, c+1), sides (r+1, c+1), (r-1, c+1)
    v = f[0];
    for (r = 0; r < rows; r++) {
        for (c = cols - 1; c >= 0; c--) {
            i = r * cols + c;
            if (!free[i]) { v[i] = 0; continue; }
            ahead = 0; side1 = 0; side2 = 0;
            if (c + 1 < cols) {
                ahead = v[i + 1];
                if (r + 1 < rows) {side1 = v[i + 1 + cols];}
                if (r - 1 >= 0) {side2 = v[i + 1 - cols];}
            }
            val = 1 + 0.5 * ahead + 0.25 * (side1 + side2);
            v[i] = val > SPACE_CAP ? SPACE_CAP : val;
        }
    }
    // +y: rows bottom to top, columns left to right; ahead (r+1, c), sides (r+1, c+1), (r+1, c-1)
    v = f[1];
    for (r = rows - 1; r >= 0; r--) {
        for (c = 0; c < cols; c++) {
            i = r * cols + c;
            if (!free[i]) { v[i] = 0; continue; }
            ahead = 0; side1 = 0; side2 = 0;
            if (r + 1 < rows) {
                ahead = v[i + cols];
                if (c + 1 < cols) {side1 = v[i + cols + 1];}
                if (c - 1 >= 0) {side2 = v[i + cols - 1];}
            }
            val = 1 + 0.5 * ahead + 0.25 * (side1 + side2);
            v[i] = val > SPACE_CAP ? SPACE_CAP : val;
        }
    }
    // -x: rows top to bottom, columns left to right; ahead (r, c-1), sides (r+1, c-1), (r-1, c-1)
    v = f[2];
    for (r = 0; r < rows; r++) {
        for (c = 0; c < cols; c++) {
            i = r * cols + c;
            if (!free[i]) { v[i] = 0; continue; }
            ahead = 0; side1 = 0; side2 = 0;
            if (c - 1 >= 0) {
                ahead = v[i - 1];
                if (r + 1 < rows) {side1 = v[i - 1 + cols];}
                if (r - 1 >= 0) {side2 = v[i - 1 - cols];}
            }
            val = 1 + 0.5 * ahead + 0.25 * (side1 + side2);
            v[i] = val > SPACE_CAP ? SPACE_CAP : val;
        }
    }
    // -y: rows top to bottom, columns left to right; ahead (r-1, c), sides (r-1, c+1), (r-1, c-1)
    v = f[3];
    for (r = 0; r < rows; r++) {
        for (c = 0; c < cols; c++) {
            i = r * cols + c;
            if (!free[i]) { v[i] = 0; continue; }
            ahead = 0; side1 = 0; side2 = 0;
            if (r - 1 >= 0) {
                ahead = v[i - cols];
                if (c + 1 < cols) {side1 = v[i - cols + 1];}
                if (c - 1 >= 0) {side2 = v[i - cols - 1];}
            }
            val = 1 + 0.5 * ahead + 0.25 * (side1 + side2);
            v[i] = val > SPACE_CAP ? SPACE_CAP : val;
        }
    }
}



function computePowerUps(m, gameState) {
    m.powerUps.length = 0;
    var pus = gameState.powerUps || [];
    for (var i = 0; i < pus.length; i++) {
        var pu = pus[i];
        var x = pu.shape.centerX, y = pu.shape.centerY;
        var nearest = -1, nearestEta = Infinity;
        for (var w = 0; w < m.worms.length; w++) {
            var worm = m.worms[w];
            if (!(worm.speed > 0)) {continue;}
            var eta = Math.hypot(worm.x - x, worm.y - y) / worm.speed;
            if (eta < nearestEta) { nearestEta = eta; nearest = w; }
        }
        m.powerUps.push({
            id: pu.id, name: pu.name, effectType: pu.effectType, affects: pu.affects,
            x: x, y: y, nearestWormIndex: nearest, nearestEta: nearestEta,
        });
    }
}

function tileIndex(m, x, y) {
    var c = (x / m.tileSize) | 0;
    var r = (y / m.tileSize) | 0;
    if (c < 0 || r < 0 || c >= m.cols || r >= m.rows) {return -1;}
    return r * m.cols + c;
}

export function tileIndexAt(m, x, y) { return tileIndex(m, x, y); }



// Space ahead (0..1) at a point for a heading (hx, hy): the cardinal fields
// blended by the heading's components (a heading between right and down mixes
// those two), over SPACE_CAP.
export var SPACE_CAP_TILES = SPACE_CAP;
export function spaceAheadAt(m, x, y, hx, hy) {
    if (m.spaceStale) { computeSpaceFields(m); m.spaceStale = false; }
    var i = tileIndex(m, x, y);
    if (i < 0) {return 0;}
    var hl = Math.sqrt(hx * hx + hy * hy);
    if (hl < 1e-6) {
        var f0 = m.spaceDir;
        return Math.max(f0[0][i], f0[1][i], f0[2][i], f0[3][i]) / SPACE_CAP;
    }
    var ux = hx / hl, uy = hy / hl;
    var f = m.spaceDir;
    var wx = Math.abs(ux), wy = Math.abs(uy);
    var vx = ux >= 0 ? f[0][i] : f[2][i];
    var vy = uy >= 0 ? f[1][i] : f[3][i];
    return (wx * vx + wy * vy) / (wx + wy) / SPACE_CAP;
}






// O(1) lookups for scoring functions. Out-of-bounds reads as fully blocked.
// `wormIndex` (optional, index into m.worms) selects that worm's own field,
// where its own head / immune trail does not count as an obstacle.
export function clearanceAt(m, x, y, wormIndex) {
    var i = tileIndex(m, x, y);
    if (i < 0) {return 0;}
    if (wormIndex !== undefined && wormIndex >= 0) {
        var entry = m.worms[wormIndex];
        if (entry) {
            var win = ensureWormClearance(m, entry);
            var c = i % m.cols, r = (i / m.cols) | 0;
            if (c >= win.c0 && c < win.c0 + win.cols && r >= win.r0 && r < win.r0 + win.rows) {
                return win.field[(r - win.r0) * win.cols + (c - win.c0)];
            }
        }
    }
    return m.clearance[i];
}
export function occupancyAt(m, x, y) {
    var i = tileIndex(m, x, y);
    return i < 0 ? Infinity : m.occupancy[i];
}

// Territory (1v1): which free tiles each of two worms reaches FIRST, walking
// the tile grid (4-neighbour steps, the same unit for both). From my position
// (mx, my) and the opponent's (ox, oy): my share of the tiles either of us can
// reach, 0..1 (a tile we reach at the same step counts half each). 0.5 when
// neither can reach anything. A classic Tron heuristic: in a duel the player
// who owns more of the board can outlast the other.
// The start tiles always count as passable (a head sits on its own paint).
export function territoryShare(m, mx, my, ox, oy) {
    var n = m.cols * m.rows;
    if (!m._terrA || m._terrA.length !== n) {
        m._terrA = new Int32Array(n);
        m._terrB = new Int32Array(n);
        m._terrQ = new Int32Array(n);
    }
    var a = m._terrA, b = m._terrB;
    var sa = tileIndex(m, mx, my), sb = tileIndex(m, ox, oy);
    if (sa < 0 && sb < 0) {return 0.5;}
    floodSteps(m, a, sa);
    floodSteps(m, b, sb);
    var mine = 0, theirs = 0;
    for (var i = 0; i < n; i++) {
        var da = a[i], db = b[i];
        if (da < 0 && db < 0) {continue;}
        if (db < 0 || (da >= 0 && da < db)) {mine += 1;}
        else if (da < 0 || db < da) {theirs += 1;}
        else {mine += 0.5; theirs += 0.5;}
    }
    var total = mine + theirs;
    return total > 0 ? mine / total : 0.5;
}

// Breadth-first step counts from tile `start` over free tiles (-1 = unreached).
function floodSteps(m, dist, start) {
    dist.fill(-1);
    if (start < 0) {return;}
    var q = m._terrQ, head = 0, tail = 0;
    var cols = m.cols, rows = m.rows, free = m.free;
    dist[start] = 0;
    q[tail++] = start;
    while (head < tail) {
        var i = q[head++];
        var d = dist[i] + 1;
        var c = i % cols;
        if (c + 1 < cols && free[i + 1] && dist[i + 1] < 0) { dist[i + 1] = d; q[tail++] = i + 1; }
        if (c > 0 && free[i - 1] && dist[i - 1] < 0) { dist[i - 1] = d; q[tail++] = i - 1; }
        if (i + cols < rows * cols && free[i + cols] && dist[i + cols] < 0) { dist[i + cols] = d; q[tail++] = i + cols; }
        if (i - cols >= 0 && free[i - cols] && dist[i - cols] < 0) { dist[i - cols] = d; q[tail++] = i - cols; }
    }
}
